// Formas de las respuestas de la API /v1 del cloud service (vistas de sus handlers).
// Fuente: veracruz-cloud-service/src/core/handlers. Si cambian allí, se cambian aquí.

export type ApiEnvelope<T> = {
	success: boolean
	code: number
	message: string
	data: T
	meta?: PageMeta
}

export type PageMeta = { total: number; page: number; limit: number; pages: number; hasMore: boolean }

export type Locale = 'es' | 'en' | 'fr'
export type GeoPoint = { latitude: number; longitude: number }
export type GeoLineString = { type: 'LineString'; coordinates: [number, number][] }

export type TreeCondition = 'healthy' | 'dry' | 'needs_pruning' | 'damaged'
export const TREE_CONDITIONS: readonly TreeCondition[] = ['healthy', 'dry', 'needs_pruning', 'damaged']
export type LatestCondition = { condition: TreeCondition; reportedAt: string } | null

// Comunidad autónoma (código INE de 2 dígitos). `/v1/regions` da las del Camino, en su orden.
export type Region = { id: string; name: string }

// Municipio: unidad administrativa (código INE de 5 dígitos, nombre oficial sin traducir).
export type Municipality = { id: string; name: string; regionId: string }

// Localidad: el pueblo por el que pasa el Camino, con sus contenidos y su punto en el mapa.
export type Locality = {
	id: string
	name: string
	municipalityId: string
	regionId: string | null
	location: GeoPoint
	description: string
	heritage: string | null
	traditions: string | null
	visitUrl: string | null
	// Variante: forma parte del Camino, fuera de su recorrido principal; no está en ninguna etapa.
	variant: boolean
}

export type Stage = {
	id: string
	order: number
	name: string
	summary: string
	distanceKm: number
	track: GeoLineString
	localityIds: string[]
	visitUrl: string | null
}

export type PoiCategory = 'heritage' | 'singular_space' | 'magic_place'
export const POI_CATEGORIES: readonly PoiCategory[] = ['heritage', 'singular_space', 'magic_place']
export type PointOfInterest = {
	id: string
	municipalityId: string
	localityId: string | null
	stageId: string | null
	category: PoiCategory
	name: string
	description: string
	location: GeoPoint
	photoUrls: string[]
}

export type StageLocality = Locality & { municipality: Municipality | null }

export type StageDetail = Stage & {
	locale: Locale
	localities: StageLocality[]
	pointsOfInterest: PointOfInterest[]
}

// En el listado: con las comunidades donde se puede plantar (orden del Camino).
export type SpeciesSummary = {
	id: string
	scientificName: string
	commonName: string
	photoUrl: string | null
	regionIds: string[]
}

export const SPECIES_REGIONAL_FIELDS = ['characteristics', 'plantingZone', 'symbology', 'culturalReferences'] as const
export type SpeciesRegionalField = (typeof SPECIES_REGIONAL_FIELDS)[number]

// Los textos de una comunidad; un campo sin texto en ella no viene.
export type SpeciesRegion = { regionId: string } & Partial<Record<SpeciesRegionalField, string>>

// En la ficha: todas sus fotos (`photoUrl` es la primera) y una entrada por comunidad donde se
// puede plantar, en el orden del Camino.
export type Species = Omit<SpeciesSummary, 'regionIds'> & { photoUrls: string[]; regions: SpeciesRegion[] }

export type TreeSummary = {
	code: string
	location: GeoPoint
	locationSource: string
	speciesId: string
	speciesName: string | null
	municipalityId: string
	stageId: string
	latestCondition: LatestCondition
}

export type TreeDetail = {
	locale: Locale
	code: string
	location: GeoPoint
	locationSource: 'planting_data' | 'surveyed'
	plantedAt: string | null
	species: { id: string; commonName: string; scientificName: string; photoUrl: string | null } | null
	municipality: { id: string; name: string } | null
	// La comunidad del municipio: con ella se abre la especie en su pestaña.
	region: { id: string; name: string } | null
	stage: { id: string; order: number; name: string } | null
	latestCondition: LatestCondition
}

export type ReportReceipt = { id: string; condition: TreeCondition; reportedAt: string; replaced: boolean }
export type ProposalReceipt = { id: string; proposedAt: string }

// Lugares mágicos (spec `magic-place-proposals`). En el mapa público, solo los aprobados.
export type MagicPlace = { id: string; name: string; photoUrl: string; location: GeoPoint }

// Para los gestores: recibida (sin revisar), aprobada (se ve en el mapa) o retirada.
export const MAGIC_PLACE_STATUSES = ['received', 'approved', 'retired'] as const
export type MagicPlaceStatus = (typeof MAGIC_PLACE_STATUSES)[number]
export type MagicPlaceProposal = MagicPlace & {
	locale: Locale
	anonymousId: string | null
	status: MagicPlaceStatus
	proposedAt: string
	statusChangedAt: string | null
}

export type Listing<T> = { locale: Locale; items: T[] }

// --- Panel de seguimiento (personal de la asociación; specs `staff-auth` y `monitoring`) ---

export type StaffUser = { name: string; email: string }
export type StaffSession = { token: string; expiresAt: string; user: StaffUser }

// Una página de un listado paginado: los elementos y la paginación del sobre (`meta`).
export type Page<T> = { items: T[]; meta: PageMeta }

export type DayRange = { from: string; to: string } // AAAA-MM-DD, ambas incluidas
export type Granularity = 'day' | 'week' | 'month'

export type TreeHealth = Record<TreeCondition, number> & { withoutReports: number; total: number }

export type TreeStatusRow = {
	treeId: string
	code: string
	speciesId: string
	municipalityId: string
	stageId: string
	condition: TreeCondition | null
	reportedAt: string | null
}

export type ReportRow = {
	id: string
	treeCode: string
	municipalityId: string
	condition: TreeCondition
	photoUrl: string | null // enlace temporal firmado
	location: GeoPoint | null
	channel: 'qr' | 'map'
	reportedAt: string
}

export type TreeHistory = { code: string; retired: boolean; reports: ReportRow[] }

export type MunicipalityActivity = {
	period: DayRange
	items: {
		municipalityId: string
		municipalityName: string | null
		reports: number
		participants: number
		trees: number
	}[]
}

export type ParticipationPoint = { period: string; reports: number; participants: number; trees: number }
export type Participation = { period: DayRange; granularity: Granularity; points: ParticipationPoint[] }
